import React, { useEffect, useState } from 'react';
import { BellRing, Play } from 'lucide-react';
import api from '@/lib/axios';

export default function MarketplaceNoticesPanel() {
  const [cfg, setCfg] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [c, h] = await Promise.all([
        api.get('/admin/marketplace/avisos/config'),
        api.get('/admin/marketplace/avisos/historico?limit=20'),
      ]);
      setCfg(c.data);
      setHistorico(h.data.items || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Falha a carregar configuração');
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    setErr('');
    try {
      const r = await api.put('/admin/marketplace/avisos/config', {
        ativo: cfg.ativo,
        dias_antes: (typeof cfg.dias_antes === 'string'
          ? cfg.dias_antes.split(',').map((d) => Number(d.trim())).filter(Boolean)
          : cfg.dias_antes),
        aviso_no_dia: cfg.aviso_no_dia,
        tipos: cfg.tipos,
        assunto: cfg.assunto,
        assunto_dia: cfg.assunto_dia,
        texto_base: cfg.texto_base,
        intervalo_minimo_horas: Number(cfg.intervalo_minimo_horas),
      });
      setCfg(r.data);
      setMsg('Configuração guardada.');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Falha a guardar');
    } finally { setBusy(false); }
  };

  const executar = async (dryRun) => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/marketplace/avisos/executar?dry_run=${dryRun}`);
      setMsg(`${dryRun ? 'Simulação' : 'Execução'}: ${r.data.candidatos} candidato(s), ${r.data.avisos} aviso(s) enviado(s).`);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Falha ao executar');
    } finally { setBusy(false); }
  };

  if (!cfg) return null;
  const toggleTipo = (tipo) => setCfg((c) => ({
    ...c,
    tipos: c.tipos.includes(tipo) ? c.tipos.filter((t) => t !== tipo) : [...c.tipos, tipo],
  }));

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="marketplace-notices-panel">
      <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
        <BellRing className="h-5 w-5 text-emerald-600" /> Avisos de validade dos anúncios
      </h2>
      {msg && <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="notices-msg">{msg}</p>}
      {err && <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="notices-error">{err}</p>}

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={cfg.ativo} onChange={(e) => setCfg((c) => ({ ...c, ativo: e.target.checked }))} data-testid="notices-active" />
          Avisos ativos
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={cfg.aviso_no_dia} onChange={(e) => setCfg((c) => ({ ...c, aviso_no_dia: e.target.checked }))} data-testid="notices-same-day" />
          Enviar também no dia da expiração
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
          Dias antes da expiração (separados por vírgula)
          <input
            className="rounded-lg border px-2 py-1.5 text-sm"
            value={Array.isArray(cfg.dias_antes) ? cfg.dias_antes.join(', ') : cfg.dias_antes}
            onChange={(e) => setCfg((c) => ({ ...c, dias_antes: e.target.value }))}
            data-testid="notices-days"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600">
          Intervalo mínimo entre avisos (horas)
          <input type="number" min="1" max="720" className="rounded-lg border px-2 py-1.5 text-sm" value={cfg.intervalo_minimo_horas} onChange={(e) => setCfg((c) => ({ ...c, intervalo_minimo_horas: e.target.value }))} data-testid="notices-interval" />
        </label>
        <div className="text-xs font-semibold text-slate-600">
          Tipos de anúncio abrangidos
          <div className="mt-1 flex flex-wrap gap-2">
            {['viatura', 'venda', 'motorista'].map((tipo) => (
              <label key={tipo} className="inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px]">
                <input type="checkbox" checked={cfg.tipos.includes(tipo)} onChange={() => toggleTipo(tipo)} data-testid={`notices-type-${tipo}`} />
                {tipo}
              </label>
            ))}
          </div>
        </div>
        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600 md:col-span-2">
          Assunto (aviso antecipado) — use {'{titulo}'} e {'{dias}'}
          <input className="rounded-lg border px-2 py-1.5 text-sm" value={cfg.assunto} onChange={(e) => setCfg((c) => ({ ...c, assunto: e.target.value }))} data-testid="notices-subject" />
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600 md:col-span-2">
          Assunto (no dia da expiração)
          <input className="rounded-lg border px-2 py-1.5 text-sm" value={cfg.assunto_dia} onChange={(e) => setCfg((c) => ({ ...c, assunto_dia: e.target.value }))} data-testid="notices-subject-day" />
        </label>
        <label className="flex flex-col gap-1 text-xs font-semibold text-slate-600 md:col-span-2">
          Texto base do email
          <textarea rows={3} className="rounded-lg border px-2 py-1.5 text-sm" value={cfg.texto_base} onChange={(e) => setCfg((c) => ({ ...c, texto_base: e.target.value }))} data-testid="notices-body" />
        </label>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button onClick={save} disabled={busy} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50" data-testid="notices-save">Guardar configuração</button>
        {msg && <span className="text-xs font-semibold text-emerald-700" data-testid="notices-save-confirm">{msg}</span>}
        <button onClick={() => executar(true)} disabled={busy} className="rounded-lg border px-3 py-1.5 text-xs font-semibold disabled:opacity-50" data-testid="notices-dry-run">Simular ciclo</button>
        <button onClick={() => executar(false)} disabled={busy} className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 disabled:opacity-50" data-testid="notices-run">
          <Play className="h-3 w-3" /> Executar agora
        </button>
      </div>

      <div className="mt-4">
        <p className="text-xs font-bold text-slate-700">Últimos avisos ({historico.length})</p>
        <ul className="mt-1 space-y-0.5 text-[11px] text-slate-600" data-testid="notices-history">
          {historico.map((h) => (
            <li key={h.id}>{(h.enviado_em || '').slice(0, 16)} · {h.tipo} · {h.estado} · anúncio {String(h.listing_id).slice(0, 8)}…</li>
          ))}
          {!historico.length && <li>Sem avisos enviados.</li>}
        </ul>
      </div>
    </section>
  );
}
